import { isValidEmail } from '@helpers/strings'
import type {
	AutocompleteRenderInputParams,
	AutocompleteRenderValueGetItemProps,
	TextFieldProps,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import React, { useCallback, useEffect, useState } from 'react'

import * as S from './styles'

interface IChipsInputpProps {
	selectedChips: (items: readonly string[]) => void
	placeholder?: string
	variant?: TextFieldProps['variant']
	id?: string
	name?: string
	label?: string
	emailValidation: boolean
	onValueChange?: (value: string) => void
	invalidEmailLabel?: string
}

const EMPTY_ARRAY = [] as const

const renderChips = (
	value: readonly string[],
	getItemProps: AutocompleteRenderValueGetItemProps<true>
) =>
	value.map((option, index) => {
		const { key, ...itemProps } = getItemProps({ index })
		return <Chip key={key} label={option} {...itemProps} />
	})

const ChipsInput = (props: IChipsInputpProps) => {
	const {
		emailValidation,
		invalidEmailLabel,
		selectedChips,
		placeholder,
		onValueChange,
		variant,
		id,
		name,
		label,
	} = props
	const [inputValue, setInputValue] = useState<string>('')
	const [selectedItem, setSelectedItem] = useState<readonly string[]>([])
	useEffect(() => {
		selectedChips(selectedItem)
	}, [selectedItem, selectedChips])

	const cleanInputValue = inputValue.trim()

	useEffect(() => {
		if (onValueChange) {
			onValueChange(cleanInputValue)
		}
	}, [cleanInputValue, onValueChange])

	const handleChange = useCallback(
		(_event: unknown, newValue: readonly string[]) => {
			setSelectedItem(newValue)
			setInputValue('')
		},
		[]
	)

	const handlePaste = useCallback(
		(value: string) => {
			const values = value.trim().split(/,+|;+|\s+/i)
			const newSelectedItem = [...selectedItem]

			for (const address of values) {
				const duplicatedValues = newSelectedItem.indexOf(address)

				if (duplicatedValues !== -1) {
					continue
				}

				if (address.replaceAll(/\s/g, '').length === 0) {
					continue
				}

				if (!emailValidation || isValidEmail(address)) {
					newSelectedItem.push(address)
				} else {
					setInputValue(address)
				}
			}

			setSelectedItem(newSelectedItem)
		},
		[selectedItem, emailValidation]
	)

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			if (
				event.key === 'Enter' ||
				event.key === ' ' ||
				event.key === ',' ||
				event.key === ';'
			) {
				let { value } = event.target as HTMLInputElement
				value = value.trim()
				const newSelectedItem = [...selectedItem]

				const duplicatedValues = newSelectedItem.indexOf(value)

				if (duplicatedValues !== -1) {
					setInputValue('')
					event.preventDefault()
					return
				}
				if (value.replaceAll(/\s/g, '').length === 0) {
					event.preventDefault()
					return
				}

				if (emailValidation && !isValidEmail(value)) {
					event.preventDefault()
					return
				}

				newSelectedItem.push(value)
				setSelectedItem(newSelectedItem)
				setInputValue('')
				event.preventDefault()
			}

			if (
				selectedItem.length > 0 &&
				inputValue.length === 0 &&
				event.key === 'Backspace'
			) {
				setSelectedItem(selectedItem.slice(0, selectedItem.length - 1))
				event.preventDefault()
			}
		},
		[emailValidation, inputValue.length, selectedItem]
	)

	const handleInputChange = useCallback(
		(_event: React.SyntheticEvent, value: string) => {
			if (value.length - inputValue.length > 1) {
				handlePaste(value)
			} else {
				if (value === ',' || value === ';') {
					return
				}
				setInputValue(value)
			}
		},
		[inputValue, handlePaste]
	)

	const isEmailInvalidErrorShown = !!(
		cleanInputValue &&
		emailValidation &&
		!isValidEmail(cleanInputValue)
	)

	const renderInput = useCallback(
		({ InputProps, ...params }: AutocompleteRenderInputParams) => (
			<S.ChipsInputContainer
				{...params}
				{...{ variant, id, name, label }}
				placeholder={placeholder}
				slotProps={{
					input: {
						...InputProps,
						onKeyDown: handleKeyDown,
					},
				}}
				multiline
				error={isEmailInvalidErrorShown}
				helperText={isEmailInvalidErrorShown ? invalidEmailLabel : undefined}
			/>
		),
		[
			variant,
			id,
			name,
			label,
			handleKeyDown,
			invalidEmailLabel,
			isEmailInvalidErrorShown,
			placeholder,
		]
	)

	return (
		<Autocomplete
			freeSolo
			multiple
			value={selectedItem as string[]}
			inputValue={inputValue}
			onInputChange={handleInputChange}
			onChange={handleChange}
			options={EMPTY_ARRAY}
			renderValue={renderChips}
			renderInput={renderInput}
		/>
	)
}

export { ChipsInput }
